import type { CtaClickSource, ToolTypeValue } from "@reactive-resume/dto";
import { SEO_ORIGIN } from "@reactive-resume/utils";

export type WaitlistJoinResponse = {
  id: string;
  position: number;
  referralCode: string;
  referralUrl: string;
  alreadyRegistered: boolean;
};

export const buildPublicToolUrl = (path: string) => {
  return new URL(path, SEO_ORIGIN).toString();
};

export const getApiErrorMessage = (payload: unknown, fallback: string) => {
  if (!payload || typeof payload !== "object") {
    return fallback;
  }

  const candidate = payload as { error?: unknown; message?: unknown };

  if (typeof candidate.error === "string" && candidate.error.trim()) {
    return candidate.error;
  }

  if (typeof candidate.message === "string" && candidate.message.trim()) {
    return candidate.message;
  }

  return fallback;
};

export const recordToolCtaClick = (tool: ToolTypeValue, source: CtaClickSource) => {
  const payload = JSON.stringify({ tool, source });

  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon("/api/tools/cta-click", blob);
      return;
    }
  } catch {
    // Fall back to fetch below.
  }

  void fetch("/api/tools/cta-click", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: payload,
    keepalive: true,
  }).catch(() => null);
};

export const joinToolWaitlist = async (email: string): Promise<WaitlistJoinResponse> => {
  const response = await fetch("/api/waitlist", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });

  const payload = (await response.json().catch(() => ({}))) as WaitlistJoinResponse | unknown;

  if (!response.ok) {
    throw new Error(getApiErrorMessage(payload, "Something went wrong. Please try again."));
  }

  return payload as WaitlistJoinResponse;
};
